import {QueryOptions} from '@apollo/client';
import {pick} from '@benchling/js-utils';
import Backbone from 'backbone';

import {parseAnalyticsUrlQueryParameters} from 'analysis-tool/AnalysisToolUrls';
import {GetAnalysis, GetAnalysisVariables} from 'analysis-tool/graphql/__generated/GetAnalysis';
import {GET_ANALYSIS_QUERY} from 'analysis-tool/graphql/GetAnalysis';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import {SingleBufferStatusBarState} from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BufferState, BufferStates} from 'Navigation';

import AnalysisTabState from './AnalysisTabState';

class AnalysisToolbarState extends Backbone.Model {}

interface AnalysisFileStateAttributes {
  // These attributes are optional - they may be undefined if the analysis is loading or inaccessible
  analysis?: GetAnalysis['analysis'];
  name?: string;
  url?: string;
  archiveRecord?: ArchiveRecord | null;
}

export default class AnalysisFileState extends ApolloFileState<
  GetAnalysis,
  GetAnalysisVariables,
  AnalysisFileStateAttributes
> {
  static fileType = ModelTypeConstants.ANALYSIS;

  static getQueryOptions(fileId: string): QueryOptions<GetAnalysisVariables> {
    return {
      query: GET_ANALYSIS_QUERY,
      variables: {
        analysisId: fileId,
      },
    };
  }

  static dataToAttributes(data: GetAnalysis): AnalysisFileStateAttributes {
    const {analysis} = data;
    return isInaccessible(analysis) ? {} : {analysis, ...pick(analysis, 'name', 'url', 'archiveRecord')};
  }

  static openFile(fileId: string): JQueryPromise<AnalysisFileState> {
    return super.openFile(fileId) as JQueryPromise<AnalysisFileState>;
  }

  archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord') ?? null;
  }

  setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set({archiveRecord});
  }

  // This is present so that when an analysis is archived due to its project being archived, an error isn't
  // thrown due to setPermissions being absent. However, we don't need to do anything here because the
  // Analysis view will poll if opened and fetch the updated permissions.
  setPermissions(): void {}

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new AnalysisToolbarState(),
      statusbar: new SingleBufferStatusBarState(),
      hideTabNav: true,
    };
  }

  initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new BufferStates([new BufferState(), new BufferState({active: false})]);
    this.set({buffers});
    buffers.addTab(new AnalysisTabState(this.getTabStateAttributes()), {active: true});
  }

  getTabStateAttributes(): {
    analysisId: string;
  } {
    return {
      analysisId: this.id,
      ...parseAnalyticsUrlQueryParameters(),
    };
  }

  // String if analysis is readable, undefined otherwise (analysis is inaccessible)
  editURL(): string | undefined {
    return this.get('url');
  }

  name(): string {
    return this.get('name') ?? 'Loading...';
  }

  registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }
}
